// File: frontend/src/pages/AdminFeedbackPage.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { NavLink } from 'react-router-dom'
import * as feedbackService from '../services/feedbackService'
import {
  FiShield,
  FiInbox,
  FiLoader,
  FiRefreshCw,
  FiAlertTriangle,
  FiBarChart2,
  FiDatabase,
  FiMessageSquare,
  FiMenu,
  FiLogOut,
  FiEdit,
  FiSave,
  FiXCircle,
  FiAward,
} from 'react-icons/fi'
import './AdminDashboardPage.css' // Re-use dashboard styles for consistency

const AdminSidebar = ({ user, logout }) => (
  <>
    <nav className="admin-sidebar-nav">
      <NavLink to="/admin/dashboard" className="admin-nav-link">
        <FiBarChart2 size={20} /> Dashboard
      </NavLink>
      <NavLink to="/admin/kb" className="admin-nav-link">
        <FiDatabase size={20} /> Knowledge
      </NavLink>
      {/* Features link - Only show to superadmins */}
      {user?.roles?.includes(
        process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
      ) && (
        <NavLink to="/admin/features" className="admin-nav-link">
          <FiAward size={20} /> Features
        </NavLink>
      )}
      <NavLink to="/admin/privacy" className="admin-nav-link">
        <FiShield size={20} /> Privacy
      </NavLink>
      <NavLink to="/admin/feedback" className="admin-nav-link">
        <FiInbox size={20} /> Feedback
      </NavLink>
      <NavLink to="/chat" className="admin-nav-link">
        <FiMessageSquare size={20} /> Chat
      </NavLink>
    </nav>
    <div className="admin-sidebar-footer">
      <div className="admin-user-info">
        {user && (
          <p className="admin-user-display-name" title={user.display_name}>
            {user.display_name || user.first_name || 'Utente'}
          </p>
        )}
      </div>
      <div className="admin-sidebar-footer-actions">
        <button onClick={logout} className="admin-icon-button small" title="Esci">
          <FiLogOut />
        </button>
      </div>
    </div>
  </>
)

const AdminFeedbackPage = () => {
  const { token, user, logout } = useAuthContext()
  const [feedbackList, setFeedbackList] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [editingFeedbackId, setEditingFeedbackId] = useState(null)
  const [editingNotes, setEditingNotes] = useState('')

  const isAdmin = user?.roles?.includes(
    process.env.REACT_APP_ADMIN_ROLE_NAME || 'admin'
  )

  const fetchFeedback = useCallback(async () => {
    if (!token || !isAdmin) return
    setLoading(true)
    setError('')
    try {
      const data = await feedbackService.getAdminFeedback(token, statusFilter)
      setFeedbackList(data)
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel recupero dei feedback.')
    } finally {
      setLoading(false)
    }
  }, [token, isAdmin, statusFilter])

  useEffect(() => {
    fetchFeedback()
  }, [fetchFeedback])

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      const updatedFeedback = await feedbackService.updateAdminFeedback(
        token,
        feedbackId,
        { status: newStatus }
      )
      setFeedbackList(prev =>
        prev.map(fb => (fb.id === feedbackId ? updatedFeedback : fb))
      )
    } catch (err) {
      setError('Impossibile aggiornare lo stato del feedback.')
    }
  }

  const handleSaveNotes = async feedbackId => {
    try {
      const updatedFeedback = await feedbackService.updateAdminFeedback(
        token,
        feedbackId,
        { admin_notes: editingNotes }
      )
      setFeedbackList(prev =>
        prev.map(fb => (fb.id === feedbackId ? updatedFeedback : fb))
      )
      setEditingFeedbackId(null)
      setEditingNotes('')
    } catch (err) {
      setError('Impossibile salvare le note.')
    }
  }

  const startEditingNotes = feedback => {
    setEditingFeedbackId(feedback.id)
    setEditingNotes(feedback.admin_notes || '')
  }

  const formatDate = dateString => new Date(dateString).toLocaleString('it-IT')

  if (!isAdmin) {
    return (
      <div className="admin-page-container">
        <main
          className="admin-main-content"
          style={{ margin: 'auto', maxWidth: '600px' }}
        >
          <div className="admin-card" style={{ textAlign: 'center' }}>
            <FiAlertTriangle size={48} className="danger" />
            <h2>Accesso Negato</h2>
            <p>Non hai i permessi per visualizzare questa pagina.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      {isSidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <img
            src="/franca-logo.png"
            alt="F.R.A.N.C.A."
            className="admin-sidebar-logo"
          />
        </div>
        <AdminSidebar user={user} logout={logout} />
      </aside>
      <main className="admin-main-content">
        <header className="admin-page-header">
          <button
            className="admin-button mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <h1>
            <FiInbox /> Gestione Feedback
          </h1>
          <div className="admin-page-header-actions">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="admin-select"
              disabled={loading}
            >
              <option value="">Tutti gli stati</option>
              <option value="new">Nuovo</option>
              <option value="read">Letto</option>
              <option value="replied_to">Risposto</option>
              <option value="archived">Archiviato</option>
            </select>
            <button
              onClick={fetchFeedback}
              disabled={loading}
              className="admin-button secondary"
            >
              <FiRefreshCw className={loading ? 'spinner-icon' : ''} /> Aggiorna
            </button>
          </div>
        </header>
        {error && <div className="admin-error-message">{error}</div>}
        <section className="admin-card">
          <h2>
            <FiMessageSquare /> Feedback Ricevuti
          </h2>
          <div className="admin-table-container">
            <table>
              <thead>
                <tr>
                  <th>Email Utente</th>
                  <th>Messaggio</th>
                  <th>Note Admin</th>
                  <th>Stato</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="admin-loading-state">
                      <FiLoader className="spinner-icon" /> Caricamento...
                    </td>
                  </tr>
                ) : feedbackList.length > 0 ? (
                  feedbackList.map(fb => (
                    <tr key={fb.id}>
                      <td>{fb.sfre_email}</td>
                      <td className="feedback-message-cell">{fb.message}</td>
                      <td className="admin-notes-cell">
                        {editingFeedbackId === fb.id ? (
                          <div className="notes-editor">
                            <textarea
                              value={editingNotes}
                              onChange={e => setEditingNotes(e.target.value)}
                              rows="3"
                            />
                            <div className="notes-actions">
                              <button
                                onClick={() => handleSaveNotes(fb.id)}
                                className="admin-icon-button"
                              >
                                <FiSave title="Salva note" />
                              </button>
                              <button
                                onClick={() => setEditingFeedbackId(null)}
                                className="admin-icon-button"
                              >
                                <FiXCircle title="Annulla" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="notes-display">
                            <p>
                              {fb.admin_notes || (
                                <span className="no-notes">Nessuna nota</span>
                              )}
                            </p>
                            <button
                              onClick={() => startEditingNotes(fb)}
                              className="admin-icon-button"
                            >
                              <FiEdit title="Modifica note" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <select
                          value={fb.status}
                          onChange={e => handleStatusChange(fb.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="new">Nuovo</option>
                          <option value="read">Letto</option>
                          <option value="replied_to">Risposto</option>
                          <option value="archived">Archiviato</option>
                        </select>
                      </td>
                      <td>{formatDate(fb.timestamp)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                      Nessun feedback trovato per il filtro selezionato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  )
}

export default AdminFeedbackPage
